import qs from 'qs'
import { AlertTitle } from '@mui/material'
import axios from 'axios'
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react'
import { api, resources } from '../services/api'
import { FormattedMessage } from 'react-intl'
import { LOCALES } from '../i18n/locales'

interface Tekster {
    BOKMAAL: Record<string, string>[]
    NORDSAMISK: Record<string, string>[]
    SOERSAMISK: Record<string, string>[]
    LULESAMISK: Record<string, string>[]
}

export interface InnsynState {
    geografi: Area[] | undefined
    isLoadingGeografi: boolean
    valgtMaalform: LOCALES
    setValgtMaalform: (locale: LOCALES) => void
    merker: Merke[] | undefined
    snitt: Snitt[] | undefined
    messages: Record<string, string> | undefined
    allMessages: Tekster
    brukerInformasjon: BrukerInformasjon | undefined
    isAuthenticated: boolean
    konfigurasjon: Konfigurasjon | undefined
    errorLoadingGeografi: boolean
    errorLoadingMerke: boolean
    errorLoadingSnitt: boolean
    errorLoadingBruker: boolean
    isLoadingMerke: boolean
    isLoadingSnitt: boolean
    isLoadingBruker: boolean
    isLoadingMessages: boolean
    alerts: Alert[]
    createAlert: (newAlert: Alert) => void
    clearAlert: (uniqueId: string) => void
    fetchBruker: () => Promise<void>
    fetchMerker: () => Promise<void>
    fetchSnitt: () => Promise<void>
    fetchGeografi: () => Promise<void>
    createLoggedInSessionAndFetchBruker: (sessionKey: string) => Promise<void>
    fetchPersonerMedNavn: (query: URLSearchParams) => Promise<Person[]>
    fetchPersonerMedMerke: (sakIdList: string[]) => Promise<Person[]>
    fetchTekster: () => Promise<void>
    fetchKonfigurasjon: () => Promise<void>
}

export const defaultState: InnsynState = {
    geografi: undefined,
    setValgtMaalform: () => undefined,
    valgtMaalform: LOCALES.BOKMAAL,
    isLoadingGeografi: true,
    isLoadingMerke: true,
    isLoadingSnitt: true,
    isLoadingBruker: true,
    isLoadingMessages: true,
    merker: undefined,
    snitt: undefined,
    messages: undefined,
    allMessages: {
        BOKMAAL: [],
        SOERSAMISK: [],
        NORDSAMISK: [],
        LULESAMISK: [],
    },
    brukerInformasjon: undefined,
    isAuthenticated: false,
    konfigurasjon: undefined,
    errorLoadingGeografi: false,
    errorLoadingMerke: false,
    errorLoadingSnitt: false,
    errorLoadingBruker: false,
    alerts: [],
    createAlert: () => undefined,
    clearAlert: () => undefined,
    fetchBruker: async () => undefined,
    fetchMerker: async () => undefined,
    fetchSnitt: async () => undefined,
    fetchGeografi: async () => undefined,
    createLoggedInSessionAndFetchBruker: async () => undefined,
    fetchPersonerMedNavn: async () => [],
    fetchPersonerMedMerke: async () => [],
    fetchTekster: async () => undefined,
    fetchKonfigurasjon: async () => undefined,
}

export const InnsynContext = createContext<InnsynState>(defaultState)

export const useInnsynContext = () => useContext(InnsynContext)

export const InnsynContextProvider = ({
                                          defaultLocale,
                                          children,
                                      }: {
    defaultLocale: LOCALES
    children: ReactNode
}) => {
    const [alerts, setAlerts] = useState<Alert[]>(defaultState.alerts)
    const [valgtMaalform, setValgtMaalform] = useState<LOCALES>(defaultLocale)
    const [geografi, setGeografi] = useState<Area[] | undefined>(
        defaultState.geografi
    )
    const [isLoadingGeografi, setIsLoadingGeografi] = useState<boolean>(
        defaultState.isLoadingGeografi
    )
    const [isLoadingMerke, setIsLoadingMerke] = useState<boolean>(
        defaultState.isLoadingMerke
    )
    const [isLoadingSnitt, setIsLoadingSnitt] = useState<boolean>(
        defaultState.isLoadingSnitt
    )
    const [isLoadingBruker, setIsLoadingBruker] = useState<boolean>(
        defaultState.isLoadingBruker
    )
    const [isLoadingMessages, setIsLoadingMessages] = useState<boolean>(
        defaultState.isLoadingMessages
    )
    const [merker, setMerker] = useState<Merke[] | undefined>(
        defaultState.merker
    )
    const [snitt, setSnitt] = useState<Snitt[] | undefined>(defaultState.snitt)
    const [messages, setMessages] = useState<Record<string, string> | undefined>(
        defaultState.messages
    )
    const [allMessages, setAllMessages] = useState<Tekster>(
        defaultState.allMessages
    )
    const [brukerInformasjon, setBrukerInformasjon] = useState<
        BrukerInformasjon | undefined
    >(defaultState.brukerInformasjon)
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
        defaultState.isAuthenticated
    )
    const [konfigurasjon, setKonfigurasjon] = useState<
        Konfigurasjon | undefined
    >(defaultState.konfigurasjon)
    const [errorLoadingGeografi, setErrorLoadingGeografi] = useState<boolean>(
        defaultState.errorLoadingGeografi
    )
    const [errorLoadingMerke, setErrorLoadingMerke] = useState<boolean>(
        defaultState.errorLoadingMerke
    )
    const [errorLoadingSnitt, setErrorLoadingSnitt] = useState<boolean>(
        defaultState.errorLoadingSnitt
    )
    const [errorLoadingBruker, setErrorLoadingBruker] = useState<boolean>(
        defaultState.errorLoadingBruker
    )

    const createAlert = useCallback((newAlert: Alert) => {
        setAlerts((prev) => {
            if (prev.some((el) => el.uniqueId === newAlert.uniqueId)) {
                return prev
            }

            return prev.concat(newAlert)
        })

        const { uniqueId, ttl } = newAlert

        if (ttl) {
            setTimeout(() => {
                setAlerts((prev) =>
                    prev.filter((el) => el.uniqueId !== uniqueId)
                )
            }, ttl)
        }
    }, [])

    const clearAlert = useCallback((uniqueId: string) => {
        setAlerts((prev) => prev.filter((el) => el.uniqueId !== uniqueId))
    }, [])

    const alertTypeHeader = useCallback((error: unknown) => {
        if (!axios.isAxiosError(error)) {
            return <FormattedMessage id='feilmelding.500_header' />
        }

        if (error.response?.status === 400) {
            return <FormattedMessage id='feilmelding.400_header' />
        }

        if (error.response?.status === 403) {
            return (
                <FormattedMessage
                    id='feilmelding.403_header'
                    values={{ feilkode: error.response.data?.uuid }}
                />
            )
        }

        if (error.response?.status === 404) {
            return error.response?.data?.uuid ? (
                <FormattedMessage
                    id='feilmelding.404_backend-header-uuid'
                    values={{ feilkode: error.response.data.uuid }}
                />
            ) : (
                <FormattedMessage id='feilmelding.404_backend-header' />
            )
        }

        return <FormattedMessage id='feilmelding.500_header' />
    }, [])

    const alertTypeContent = useCallback((error: unknown) => {
        if (!axios.isAxiosError(error)) {
            return null
        }

        const status = error.response?.status
        const uuid = error.response?.data?.uuid

        if (status === 400 || status === 500 || (status === 404 && uuid)) {
            return (
                <FormattedMessage
                    id='feilmelding.content'
                    values={{ feilkode: uuid }}
                />
            )
        }

        return null
    }, [])

    const mapToMessages = useCallback(
        (data: Tekster) => {
            let languageKey: keyof Tekster = 'BOKMAAL'

            Object.entries(LOCALES).forEach(([key, value]) => {
                if (value === valgtMaalform) {
                    languageKey = key as keyof Tekster
                }
            })

            const teksterList = data[languageKey]?.map(
                (obj: Record<string, string>) => ({
                    [obj.noekkel]: obj.spraaktekst,
                })
            )

            return teksterList ? Object.assign({}, ...teksterList) : {}
        },
        [valgtMaalform]
    )

    const personTilPersonObjekt = useCallback((res: any[]): Person[] => {
        return res.map((person) => ({
            personnavn: {
                fornavn: person.personnavn.fornavn,
                etternavn: person.personnavn.etternavn,
            },
            organisasjon: {
                orgnavn: person.organisasjon.orgnavn,
                orgnr: person.organisasjon.orgnr,
            },
            siidaandelNavn: person.siidaandelNavn,
            id: person.id,
        }))
    }, [])

    const fetchGeografi = useCallback(async () => {
        setIsLoadingGeografi(true)
        setErrorLoadingGeografi(false)

        try {
            const res = await api.get(resources.geografi)

            if (res.status === 200) {
                setGeografi(res.data)
            }
        } catch (error) {
            setErrorLoadingGeografi(true)
            createAlert({
                body: (
                    <>
                        <AlertTitle>{alertTypeHeader(error)}</AlertTitle>
                        {alertTypeContent(error)}
                    </>
                ),
                severity: 'error',
                uniqueId: 'fetch-geografi-error',
            })
        } finally {
            setIsLoadingGeografi(false)
        }
    }, [alertTypeContent, alertTypeHeader, createAlert])

    const fetchMerker = useCallback(async () => {
        setIsLoadingMerke(true)
        setErrorLoadingMerke(false)

        try {
            const res = await api.get(resources.merke)

            if (res.status === 200) {
                setMerker(res.data.merker)
            }
        } catch (error) {
            setErrorLoadingMerke(true)
            createAlert({
                body: (
                    <>
                        <AlertTitle>{alertTypeHeader(error)}</AlertTitle>
                        {alertTypeContent(error)}
                    </>
                ),
                severity: 'error',
                uniqueId: 'fetch-merker-error',
            })
        } finally {
            setIsLoadingMerke(false)
        }
    }, [alertTypeContent, alertTypeHeader, createAlert])

    const fetchSnitt = useCallback(async () => {
        setIsLoadingSnitt(true)
        setErrorLoadingSnitt(false)

        try {
            const res = await api.get(resources.snitt)

            if (res.status === 200) {
                setSnitt(res.data.snitt)
            }
        } catch (error) {
            setErrorLoadingSnitt(true)
            createAlert({
                body: (
                    <>
                        <AlertTitle>{alertTypeHeader(error)}</AlertTitle>
                        {alertTypeContent(error)}
                    </>
                ),
                severity: 'error',
                uniqueId: 'fetch-snitt-error',
            })
        } finally {
            setIsLoadingSnitt(false)
        }
    }, [alertTypeContent, alertTypeHeader, createAlert])

    const fetchBruker = useCallback(async () => {
        setIsLoadingBruker(true)
        setErrorLoadingBruker(false)

        try {
            const res = await api.get(resources.bruker)

            if (res.status === 200 && res.data?.foedselsnummer) {
                setBrukerInformasjon(res.data)
                setIsAuthenticated(true)

                if (!res.data.harTilgang) {
                    createAlert({
                        body: (
                            <>
                                <AlertTitle>
                                    <FormattedMessage id='feilmelding.missing-rights-to-view-name' />
                                </AlertTitle>
                            </>
                        ),
                        severity: 'info',
                        uniqueId: 'fetch-bruker-no-access-info',
                        ttl: 6000,
                    })
                }

                return
            }

            setBrukerInformasjon(undefined)
            setIsAuthenticated(false)
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                const status = error.response?.status

                if (status === 401) {
                    return
                }
            }

            setBrukerInformasjon(undefined)
            setIsAuthenticated(false)
            setErrorLoadingBruker(true)

            createAlert({
                body: (
                    <>
                        <AlertTitle>{alertTypeHeader(error)}</AlertTitle>
                        {alertTypeContent(error)}
                    </>
                ),
                severity: 'error',
                uniqueId: 'fetch-bruker-error',
            })
        } finally {
            setIsLoadingBruker(false)
        }
    }, [alertTypeContent, alertTypeHeader, createAlert])

    const createLoggedInSessionAndFetchBruker = useCallback(
        async (sessionKey: string) => {
            try {
                await api.get(`${resources.session}?Tempkey=${sessionKey}`)
            } catch (error) {
                createAlert({
                    body: (
                        <>
                            <AlertTitle>{alertTypeHeader(error)}</AlertTitle>
                            {alertTypeContent(error)}
                        </>
                    ),
                    severity: 'error',
                    uniqueId: 'create-session-and-fetch-bruker-error',
                })
            } finally {
                await fetchBruker()
            }
        },
        [alertTypeContent, alertTypeHeader, createAlert, fetchBruker]
    )

    const fetchTekster = useCallback(async () => {
        setIsLoadingMessages(true)

        try {
            const res = await api.get(resources.tekster)

            if (res.status === 200) {
                setAllMessages(res.data)
                setMessages(mapToMessages(res.data))
            }
        } catch (error) {
            createAlert({
                body: (
                    <>
                        <AlertTitle>{alertTypeHeader(error)}</AlertTitle>
                        {alertTypeContent(error)}
                    </>
                ),
                severity: 'error',
                uniqueId: 'fetch-tekster-error',
            })
        } finally {
            setIsLoadingMessages(false)
        }
    }, [alertTypeContent, alertTypeHeader, createAlert, mapToMessages])

    const fetchPersonerMedNavn = useCallback(
        async (query: URLSearchParams): Promise<Person[]> => {
            try {
                const res = await api.post(
                    resources.person.navn,
                    Object.fromEntries(query),
                    {}
                )

                if (res.status === 200) {
                    return personTilPersonObjekt(res.data)
                }
            } catch (error) {
                createAlert({
                    body: (
                        <>
                            <AlertTitle>{alertTypeHeader(error)}</AlertTitle>
                            {alertTypeContent(error)}
                        </>
                    ),
                    severity: 'error',
                    uniqueId: 'fetch-person-med-navn-error',
                })
            }

            return []
        },
        [
            alertTypeContent,
            alertTypeHeader,
            createAlert,
            personTilPersonObjekt,
        ]
    )

    const fetchPersonerMedMerke = useCallback(
        async (sakIdList: string[]): Promise<Person[]> => {
            try {
                const res = await api.get(resources.person.merker, {
                    params: {
                        sakIdList,
                    },
                    paramsSerializer: (params) =>
                        qs.stringify(params, { arrayFormat: 'repeat' }),
                })

                if (res.status === 200) {
                    return personTilPersonObjekt(res.data)
                }
            } catch (error) {
                createAlert({
                    body: (
                        <>
                            <AlertTitle>{alertTypeHeader(error)}</AlertTitle>
                            {alertTypeContent(error)}
                        </>
                    ),
                    severity: 'error',
                    uniqueId: 'fetch-person-med-merke-error',
                })
            }

            return []
        },
        [
            alertTypeContent,
            alertTypeHeader,
            createAlert,
            personTilPersonObjekt,
        ]
    )

    const fetchKonfigurasjon = useCallback(async () => {
        try {
            const res = await api.get(resources.konfigurasjon)

            if (res.status === 200) {
                setKonfigurasjon(res.data)
            }
        } catch (error) {
            createAlert({
                body: (
                    <>
                        <AlertTitle>{alertTypeHeader(error)}</AlertTitle>
                        {alertTypeContent(error)}
                    </>
                ),
                severity: 'error',
                uniqueId: 'fetch-konfigurasjon-error',
            })
        }
    }, [alertTypeContent, alertTypeHeader, createAlert])

    useEffect(() => {
        void fetchTekster()
        void fetchGeografi()
        void fetchMerker()
        void fetchSnitt()
        void fetchKonfigurasjon()
    }, [
        fetchTekster,
        fetchGeografi,
        fetchMerker,
        fetchSnitt,
        fetchKonfigurasjon,
    ])

    useEffect(() => {
        if (typeof valgtMaalform !== 'string') {
            return
        }

        localStorage.setItem('reinmerker-innsyn-locale', valgtMaalform)
        setMessages(mapToMessages(allMessages))
    }, [valgtMaalform, allMessages, mapToMessages])

    return (
        <InnsynContext.Provider
            value={{
                geografi,
                valgtMaalform,
                setValgtMaalform,
                isLoadingGeografi,
                merker,
                snitt,
                messages,
                allMessages,
                brukerInformasjon,
                isAuthenticated,
                konfigurasjon,
                isLoadingSnitt,
                isLoadingBruker,
                isLoadingMessages,
                errorLoadingSnitt,
                errorLoadingGeografi,
                errorLoadingMerke,
                errorLoadingBruker,
                isLoadingMerke,
                alerts,
                createAlert,
                clearAlert,
                fetchBruker,
                fetchMerker,
                fetchSnitt,
                fetchGeografi,
                createLoggedInSessionAndFetchBruker,
                fetchPersonerMedNavn,
                fetchPersonerMedMerke,
                fetchTekster,
                fetchKonfigurasjon,
            }}>
            {children}
        </InnsynContext.Provider>
    )
}

export default InnsynContextProvider